import { K, isTrueStringMatch, convertBlankDefault, convertBlankDefaultToZero } from "../utils";

// https://github.com/gocanvas/android_sdk/blob/3ddd05c7578ede8a8175b4d438bdb20b8f47001a/canvasSdkSample/src/main/java/com/gocanvas/utils/ConditionBools.java#L236
export const checkBoxTrue = ['true', 't', 'yes', 'y', 'checked', '1'];
// these are not represented in the mobile app
// but will roughly "work"/be converted to false for 0-5 type comparisons
export const checkBoxFalse = ['false', '0', 'unchecked', 'f', 'no', 'n'];

// defaultMethod :: Any -> true
const defaultMethod = K(true);

// lowerCaseBinaryFn :: Function -> Function(Any, Any) -> Boolean
export const lowerCaseBinaryFn = fn => (a, b) => fn(convertBlankDefault(a).toLowerCase(), convertBlankDefault(b).toLowerCase());
export const numericCaseBinaryFn = fn => (a, b) => fn(convertBlankDefaultToZero(a), convertBlankDefaultToZero(b));

// conditionMethods :: Keyed Object of binary functions, each of the form:
// (thisFieldsValue, otherFieldsValue) -> Boolean
export const conditionMethods = {
  0: lowerCaseBinaryFn((a, b) => a === b),
  // ==
  1: lowerCaseBinaryFn((a, b) => a !== b),
  // !=
  2: numericCaseBinaryFn((a, b) => a < b),
  // <
  3: numericCaseBinaryFn((a, b) => a <= b),
  // <=
  4: numericCaseBinaryFn((a, b) => a > b),
  // >
  5: numericCaseBinaryFn((a, b) => a >= b),
  // >=
  6: lowerCaseBinaryFn((a, b) => a.includes(b)),
  // string contains...
  7: lowerCaseBinaryFn((a, b) => a.startsWith(b)),
  // string starts with...
  8: lowerCaseBinaryFn((a, b) => a.endsWith(b)),
  // string ends with...
  9: lowerCaseBinaryFn((a, b) => !a.includes(b)),
  // string doesn't contain...
  10: lowerCaseBinaryFn((a, b) => !a.startsWith(b)),
  // string doesn't start with...
  11: lowerCaseBinaryFn((a, b) => !a.endsWith(b)) // string doesn't end with...
};

// TODO: consider using these for text, as they mirror what mobile does
const textConditionMethods = {
  ...conditionMethods,
  2: lowerCaseBinaryFn((a, b) => a < b),
  // <
  3: lowerCaseBinaryFn((a, b) => a <= b),
  // <=
  4: lowerCaseBinaryFn((a, b) => a > b),
  // >
  5: lowerCaseBinaryFn((a, b) => a >= b) // >=
};

// areAllAndConditionsValid :: Array -> Boolean
// can break early on the first false case
export const areAllAndConditionsValid = conditions => {
  for (const {
    operator,
    valid
  } of conditions) {
    if (operator !== 1 && !valid) {
      return false;
    }
  }
  return true;
};

// check entry values then loop values for matches in one pass
// getFieldValue :: (String, Array, String) -> Object({ value, type })
export const getFieldValue = (entryGuid, values, rowGuid) => {
  const {
    value,
    type
  } = values.find(({
    guid,
    parentGuid,
    rowGuid: entryRowGuid,
    keyField
  }) => {
    if (!rowGuid || !entryRowGuid && !keyField) {
      // either the conditional field isn't in a loop or the else the
      // target field is external to the current loop row & not a keyfield
      // for either case, we can just match on guid
      // NOTE/TODO: this logic won't work for interactive preview of advanced loops
      return guid === entryGuid;
    }
    // target field/keyfield is in the current loop row
    return entryRowGuid === rowGuid && parentGuid === entryGuid;
  }) || {};
  return {
    value,
    type
  };
};

// evaluateCondition :: (Object, Array, String) -> True || Object
export const evaluateCondition = (condition, values, filterByRowGuid = false) => {
  if (!condition) {
    return true;
  }
  let {
    original_type_id,
    entry: entryGuid,
    value,
    operator
  } = condition;
  if (original_type_id === undefined) {
    return true;
  }
  let {
    value: fieldValue,
    type
  } = getFieldValue(entryGuid, values, filterByRowGuid);
  let conditionMethod = conditionMethods[original_type_id] || defaultMethod;
  if (type === 'Checkbox') {
    value = String(value).toLowerCase();
    if (value && original_type_id < 6) {
      value = checkBoxTrue.includes(value) ? 'True' : 'False';
    }
    conditionMethod = textConditionMethods[original_type_id] || defaultMethod;
  }
  return {
    valid: conditionMethod(fieldValue, value),
    operator
  };
};

// checkCombinedConditions :: (Array) -> Boolean
export const checkCombinedConditions = conditions => {
  const validOrCondition = conditions.find(({
    operator,
    valid
  }) => valid && operator === 1);
  if (validOrCondition) {
    return true;
  }
  return areAllAndConditionsValid(conditions);
};

// given a sheet with an specific id and an array of conditions and form values, filter out field conditions and determine if a given sheet is valid/allowed in the final output
// conditionalScreenFilter :: (Object, Array, Array) -> Boolean
export const conditionalScreenFilter = ({
  id
}, conditions, previewValues) => {
  const screenConditions = conditions.filter(({
    parentEntryGuid,
    sheet_id = ''
  }) => {
    return !parentEntryGuid && isTrueStringMatch(sheet_id, id);
  });
  const conditionStates = screenConditions.map(condition => evaluateCondition(condition, previewValues));
  return checkCombinedConditions(conditionStates);
};

// TODO: optimize for loops, where we only need to consider fields/values from active rows & values from fields external to rows
// given an entryID and an array of field values and an array of conditions, determine if this
// field is valid/allowed in the final output
// conditionCheck :: (String, Array, Array) -> Boolean
export const conditionCheck = (entryId, entries, conditions, filterByRowGuid = false) => {
  // only consider conditions that relate to the field
  const entryConditions = conditions.filter(({
    parentEntryGuid = ''
  }) => {
    return parentEntryGuid === entryId;
  });
  const conditionStates = entryConditions.map(condition => evaluateCondition(condition, entries, filterByRowGuid));
  return checkCombinedConditions(conditionStates);
};